/**
 * Loudness mathematics for ETERNALLY playback.
 *
 * Everything here is pure and deterministic: the player never measures,
 * it only applies gains derived from values measured at ingest. That is
 * the foundation of runtime loudness correctness.
 *
 * Model:
 *   1. Per-track makeup gain lifts each track to the target loudness.
 *   2. Active tracks are summed with equal-power weighting 1/sqrt(N)
 *      (uncorrelated-source assumption), so one track solo and sixteen
 *      tracks together land at the same perceived level.
 *   3. A headroom guard trims the master when the worst-case true peak
 *      of the combination would exceed the ceiling. Worst case assumes
 *      fully correlated peaks (linear amplitude sum), which no real
 *      combination can exceed, so no lookahead limiter is needed.
 */

/** Playback loudness target for web delivery. */
export const DEFAULT_TARGET_LUFS = -16;

/** True peak ceiling in dBTP. Combinations must never predict above this. */
export const TRUE_PEAK_CEILING_DBTP = -1;

/** The measured facts a gain decision needs; a subset of TrackManifest. */
export interface MeasuredTrack {
  lufs: number;
  truePeakDb: number;
}

export function dbToLinear(db: number): number {
  assertFinite(db, 'db');
  return 10 ** (db / 20);
}

export function linearToDb(gain: number): number {
  assertFinite(gain, 'gain');
  if (gain < 0) {
    throw new RangeError(`linearToDb: gain must be non-negative, got ${gain}`);
  }
  return 20 * Math.log10(gain);
}

/**
 * Makeup gain in dB that lifts a track from its measured integrated
 * loudness to the playback target.
 */
export function trackGainDb(
  track: Pick<MeasuredTrack, 'lufs'>,
  targetLufs: number = DEFAULT_TARGET_LUFS,
): number {
  assertFinite(track.lufs, 'track.lufs');
  assertFinite(targetLufs, 'targetLufs');
  return targetLufs - track.lufs;
}

/**
 * Linear master weighting for N simultaneously active tracks.
 *
 * Uncorrelated sources add in power, not amplitude, so weighting each
 * contribution by 1/sqrt(N) keeps the summed power constant: the
 * perceived level of the combination equals the perceived level of a
 * single track at target. Zero active tracks yields unity because there
 * is nothing to normalise and the node must still hold a finite value.
 */
export function combinationTrim(activeTracks: readonly MeasuredTrack[]): number {
  const n = activeTracks.length;
  if (n === 0) {
    return 1;
  }
  return 1 / Math.sqrt(n);
}

/** combinationTrim expressed in dB: -10 * log10(N). */
export function combinationTrimDb(activeTracks: readonly MeasuredTrack[]): number {
  return linearToDb(combinationTrim(activeTracks));
}

/**
 * Worst-case true peak in dBTP of the active combination after makeup
 * gain and combination trim.
 *
 * Fully correlated signals peak at the sum of their linear amplitudes,
 * and no combination can exceed that bound, so a prediction made from
 * stored per-track dBTP values is a hard guarantee, not an estimate.
 */
export function predictedTruePeakDb(
  activeTracks: readonly MeasuredTrack[],
  targetLufs: number = DEFAULT_TARGET_LUFS,
): number {
  if (activeTracks.length === 0) {
    return -Infinity;
  }
  const trim = combinationTrim(activeTracks);
  let linearPeakSum = 0;
  for (const track of activeTracks) {
    assertFinite(track.truePeakDb, 'track.truePeakDb');
    const peakAfterGainDb = track.truePeakDb + trackGainDb(track, targetLufs);
    linearPeakSum += dbToLinear(peakAfterGainDb);
  }
  return linearToDb(linearPeakSum * trim);
}

/**
 * Linear safety trim for the master node. Unity when the worst-case
 * peak stays below the ceiling; otherwise exactly the overage, so the
 * trimmed combination predicts precisely at the ceiling. Deterministic:
 * the same set of active tracks always yields the same trim.
 */
export function masterSafetyTrim(
  activeTracks: readonly MeasuredTrack[],
  targetLufs: number = DEFAULT_TARGET_LUFS,
  ceilingDbTp: number = TRUE_PEAK_CEILING_DBTP,
): number {
  assertFinite(ceilingDbTp, 'ceilingDbTp');
  const predicted = predictedTruePeakDb(activeTracks, targetLufs);
  if (predicted <= ceilingDbTp) {
    return 1;
  }
  return dbToLinear(ceilingDbTp - predicted);
}

function assertFinite(value: number, name: string): void {
  if (!Number.isFinite(value)) {
    throw new TypeError(`${name} must be finite, got ${value}`);
  }
}
